import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { createSubject } from '../../../redux/features/assessmentCmsSlice';

/**
 * Create a paper.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * The Class 5-10 and +1/+2 papers ship with no rows at all — deliberately, so
 * nobody inherits questions somebody else guessed at. An employee builds them
 * here: paper, then levels, then questions.
 *
 * ---------------------------------------------------------------------------
 * THE ID IS DERIVED, NOT ASKED FOR
 *
 * Every paper needs a short url-safe id: it is what the browser sends as
 * `?subject=` and what every stored answer is filed under. Asking a marketer
 * for a "key name" invites `Social Science` with a space in it, so the server
 * slugifies the label instead — and the preview below shows the result before
 * they commit, because the id is **immutable once created**. Renaming a paper
 * later changes only its display label.
 */
const AddPaperDialog = ({ open, onClose, onCreated, tracks, defaultTrack }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { saving } = useSelector((state) => state.assessmentCms);

  const [label, setLabel] = useState('');
  const [track, setTrack] = useState(defaultTrack || 'secondary');

  const slug = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);

  const selected = tracks.find((t) => t.value === track);

  const submit = async () => {
    try {
      const created = await dispatch(createSubject({ label, track })).unwrap();
      // Report the id the server actually settled on. It may carry the paper
      // type on the end, and it is permanent, so seeing it once here is the
      // only chance to notice it is not what the preview above said.
      const keyName = created?.data?.subject?.keyName || created?.subject?.keyName;
      enqueueSnackbar(
        keyName && keyName !== slug
          ? `"${label}" created as "${keyName}". Add its levels next.`
          : `"${label}" created. Add its levels next.`,
        { variant: 'success' },
      );
      setLabel('');
      onCreated();
      onClose();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not create the paper', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a paper</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Which students sit this paper"
            value={track}
            onChange={(e) => setTrack(e.target.value)}
          >
            {tracks.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            size="small"
            label="Subject name"
            placeholder="Science"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            helperText="What a student sees on the subject picker."
          />

          {slug && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.neutral',
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Permanent id
              </Typography>
              <Chip
                size="small"
                label={slug}
                sx={{ mt: 0.5, fontFamily: 'monospace' }}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Built from the name and fixed for good — it is what every answer is filed
                under. Renaming the paper later changes only what students see.
              </Typography>
              {/* The id shown is a preview, not a promise. Only the server knows
                  what is already taken, and it appends the paper type rather
                  than refusing a name that is legitimately shared — "English"
                  for Class 1-4 and "English" for +1/+2 are different papers. */}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Another paper may already be using this id — that is fine and does not stop
                you. It will become <code>{slug}-…</code> with the paper type on the end, and
                you will be told the final id once it is created. The name itself can repeat
                as often as you like.
              </Typography>
            </Box>
          )}

          {selected && !selected.isLadder && (
            <Alert severity="warning">
              There is normally exactly one {selected.label} paper and it already exists.
              A second one will not break anything, but only one can be the paper the
              funnel actually serves.
            </Alert>
          )}

          {selected?.isLadder && (
            <Alert severity="info">
              A new paper starts empty. Add its levels next — each level is one rung of the
              ladder a student climbs, and a student only reaches the next rung by passing
              the one before it.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || !slug}>
          {saving ? 'Creating…' : 'Create paper'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPaperDialog;
